import { useMemo } from 'react';
import { parseLocalDate } from '../utils/dateUtils';
import { getCalendarColor } from '../components/schedule/CalendarsList';
import { getHolidaysForYearRange } from '../utils/holidayUtils';

/**
 * Hook to generate holiday events for the calendar
 */
export const useHolidayEvents = () => {
  return useMemo(() => {
    const currentYear = new Date().getFullYear();
    const holidays = getHolidaysForYearRange(currentYear - 1, currentYear + 2);

    return holidays.map((holiday) => {
      const dateStr = holiday.date.toISOString().split('T')[0];
      const displayTitle = holiday.emoji ? `${holiday.emoji} ${holiday.title}` : holiday.title;
      return {
        id: `holiday-${holiday.title}-${dateStr}`,
        title: displayTitle,
        start: dateStr,
        allDay: true,
        extendedProps: {
          type: 'holiday'
        },
        backgroundColor: '#FF6B6B',
        borderColor: '#FF6B6B',
        editable: false,
        classNames: ['calendar-holiday-event']
      };
    });
  }, []);
};

/**
 * Hook to generate camp events from schedules
 */
export const useCampEvents = (schedules: any[], selectedScheduleIds: string[]) => {
  return useMemo(() => {
    const campEvents: any[] = [];

    schedules.forEach((sched, index) => {
      if (!selectedScheduleIds.includes(sched.uuid)) return;

      const color = getCalendarColor(index);
      const scheduledCamps = sched.scheduled_camps || [];

      scheduledCamps.forEach((scheduled: any) => {
        // Calculate end date - add 1 day since FullCalendar's end is exclusive for all-day events
        const endDate = parseLocalDate(scheduled.camp_date.end_date);
        endDate.setDate(endDate.getDate() + 1);
        const endDateStr = endDate.toISOString().split('T')[0];

        campEvents.push({
          id: `camp-${sched.uuid}-${scheduled.camp.id}-${scheduled.camp_date.id}`,
          title: `🏕️ ${scheduled.camp.name}`,
          start: scheduled.camp_date.start_date,
          end: endDateStr,
          allDay: true,
          extendedProps: {
            type: 'camp',
            campDateId: scheduled.camp_date.id,
            camp: scheduled.camp,
            scheduleUuid: sched.uuid
          },
          backgroundColor: color,
          borderColor: color
        });
      });
    });

    return campEvents;
  }, [schedules, selectedScheduleIds]);
};

/**
 * Hook to generate custom events from schedules
 */
export const useCustomEvents = (schedules: any[], selectedScheduleIds: string[]) => {
  return useMemo(() => {
    const customEvents: any[] = [];

    schedules.forEach((sched, index) => {
      if (!selectedScheduleIds.includes(sched.uuid)) return;

      const color = getCalendarColor(index);
      const events = sched.custom_events || [];

      events.forEach((event: any) => {
        const endDate = parseLocalDate(event.end_date);
        endDate.setDate(endDate.getDate() + 1);
        const endDateStr = endDate.toISOString().split('T')[0];

        customEvents.push({
          id: `event-${sched.uuid}-${event.id}`,
          title: event.title,
          start: event.start_date,
          end: endDateStr,
          allDay: true,
          extendedProps: {
            type: 'custom',
            eventId: event.id,
            eventData: event,
            scheduleUuid: sched.uuid
          },
          backgroundColor: color,
          borderColor: color
        });
      });
    });

    return customEvents;
  }, [schedules, selectedScheduleIds]);
};

/**
 * Hook to combine all calendar events
 */
export const useAllCalendarEvents = (schedules: any[], selectedScheduleIds: string[]) => {
  const holidayEvents = useHolidayEvents();
  const campEvents = useCampEvents(schedules, selectedScheduleIds);
  const customEvents = useCustomEvents(schedules, selectedScheduleIds);

  return useMemo(() => {
    return [...holidayEvents, ...campEvents, ...customEvents];
  }, [holidayEvents, campEvents, customEvents]);
};
